import React, { useEffect } from "react";
import { Stack } from "react-bootstrap";
import { useNavigate } from "react-router-dom";
import { useTranslation } from "react-i18next";

import "./ManageSubscription.scss";
import HeaderWithTitleAndBackButton from "../../components/Headers/HeaderWithTitleAndBackButton/HeaderWithTitleAndBackButton";
import TagIcon from "../../components/icons/TagIcon";
import CalendarIcon from "../../components/icons/CalendarIcon";
import CrownIcon from "../../components/icons/CrownIcon";
import DotIcon from "../../components/icons/DotIcon";
import useSubscription from "../../react-query/queries/use-subscription-status";
import { ROUTES } from "../../utils/constants";
import {
  getPlanCopyKeys,
  usePaywallPricing,
} from "../../hooks/usePaywallPricing";
import { calculateSavings } from "../../utils/pricing";
import {
  extractOgTitleFromMainTitle,
  renderHeaders,
} from "../../utils/renderHeaders";
import useDisclosure from "../../hooks/useDisclosure";
import ConfirmUpgradeDrawer from "../../components/manage-subscription/ConfirmUpgradeDrawer/ConfirmUpgradeDrawer";
import GemIcon from "../../components/icons/GemIcon";
import PrimaryActionButton from "../../components/Buttons/PrimaryActionButton/PrimaryActionButton";
import ChevronRightIcon from "../../components/icons/ChevronRightIcon";

function formatDate(date: Date) {
  if (!date) return "";
  return date.toLocaleDateString("en-GB", {
    day: "numeric",
    month: "short",
    year: "numeric",
  });
}

interface SubscriptionItemProps {
  Icon: React.FC;
  title: string;
  description: React.ReactNode;
  tag?: string;
}

function SubscriptionItem({
  Icon,
  title,
  description,
  tag,
}: SubscriptionItemProps) {
  return (
    <Stack
      direction="horizontal"
      className="justify-content-between align-items-center subscription-item"
    >
      <Stack direction="horizontal" className="subscription-details">
        <span className="icon-container">
          <Icon />
        </span>
        <Stack gap={1} className="align-items-start subscription-item-text">
          <h4 className="title">{title}</h4>
          <div className="description">{description}</div>
        </Stack>
      </Stack>
      {tag && <p className="tag">{tag}</p>}
    </Stack>
  );
}

export default function ManageSubscription() {
  const navigate = useNavigate();
  const { t } = useTranslation("common", { keyPrefix: "profile" });
  const { t: tCommon } = useTranslation("common");
  const { t: tMeta } = useTranslation("metadata");
  const {
    isSubscribed,
    isLoading: isSubscriptionLoading,
    subscriptionData,
  } = useSubscription();
  const { productIdentifier, expirationDate } = subscriptionData ?? {};
  const formattedExpirationDate = expirationDate
    ? formatDate(expirationDate)
    : "";
  const { calculatePricing, findPackageByProductId, yearlyPackage } =
    usePaywallPricing();
  const currentPackage = findPackageByProductId(productIdentifier);
  const pricing = calculatePricing(currentPackage);
  const currentPlanCopy = getPlanCopyKeys(currentPackage?.packageType);

  const canUpgradeToYearly = Boolean(
    currentPackage &&
    yearlyPackage &&
    currentPackage.identifier !== yearlyPackage.identifier
  );

  // To calculate savings relative to the user current plan, not the monthly baseline
  const discountAmount = canUpgradeToYearly
    ? (calculateSavings(currentPackage, yearlyPackage) ?? "")
    : "";

  const {
    isOpen: isConfirmUpgradeDrawerOpen,
    onOpen: openConfirmUpgradeDrawer,
    onClose: closeConfirmUpgradeDrawer,
  } = useDisclosure();

  useEffect(() => {
    const redirectTimeout = setTimeout(() => {
      if (isSubscribed === false) navigate(ROUTES.ROOT.PROFILE);
    }, 100);

    return () => {
      clearTimeout(redirectTimeout);
    };
  }, [isSubscribed, isSubscriptionLoading, navigate]);

  return (
    <>
      {renderHeaders({
        title: tMeta("sigma_manage_subscription_title"),
        description: tMeta("sigma_manage_subscription_description"),
        ogTitle: extractOgTitleFromMainTitle(
          tMeta("sigma_manage_subscription_title")
        ),
      })}
      <div className="manage-subscription-page">
        <Stack gap={4}>
          <HeaderWithTitleAndBackButton
            title={{ text: "Manage subscription" }}
            goToPreviousPage
          />

          <Stack className="align-items-start subscription-details">
            <h3 className="section-title">{t("your_subscription")}</h3>
            <Stack gap={3} className="subscription-details-card">
              <SubscriptionItem
                title={t("plan")}
                description={
                  <p className="d-flex gap-1 align-items-center">
                    <span>{t("all_access")}</span>
                    {currentPlanCopy && (
                      <>
                        <DotIcon fill="#262626" />
                        <span className="text-capitalize">
                          {tCommon(currentPlanCopy.labelKey)}
                        </span>
                      </>
                    )}
                  </p>
                }
                tag={t("active")}
                Icon={CrownIcon}
              />
              <SubscriptionItem
                title={t("billing")}
                description={pricing.billingDisplay}
                Icon={TagIcon}
              />
              <SubscriptionItem
                title={t("next_payment")}
                description={formattedExpirationDate}
                Icon={CalendarIcon}
              />
            </Stack>
          </Stack>

          {canUpgradeToYearly && (
            <Stack className="upgrade-section">
              <h3 className="section-title">{t("for_you")}</h3>
              <button
                className="upgrade-btn"
                onClick={openConfirmUpgradeDrawer}
              >
                <span className="btn-main-content">
                  <GemIcon />
                  <span className="btn-copy">
                    {t("switch_to_yearly_and_save_amount", {
                      amount: discountAmount,
                    })}
                  </span>
                </span>
                <span>
                  <ChevronRightIcon fill="rgba(86, 40, 3, 0.24)" />
                </span>
              </button>
            </Stack>
          )}
        </Stack>

        <Stack gap={3} className="subscription-actions">
          <PrimaryActionButton
            isExternal
            as="a"
            href="https://forms.fillout.com/t/1NhubqvMM7us"
            text={t("cancel_subscription")}
            className="cancel-subscription-btn"
          />
        </Stack>
      </div>

      <ConfirmUpgradeDrawer
        isOpen={isConfirmUpgradeDrawerOpen}
        onClose={closeConfirmUpgradeDrawer}
        discountAmount={discountAmount}
      />
    </>
  );
}
